/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file Component for showing a table inside a dashboard widget
 * @module Epic.AppOrchard.Dashboard.WidgetTable
 */

import { Box, Center, List } from "@chakra-ui/layout";
import { useBreakpointValue } from "@chakra-ui/react";
import { PaginatedList } from "ao/components/Core";
import React, { FC, memo } from "react";

export interface IWidgetTableProps {
	noRecordsMessage: string;
	children: React.ReactNode;
	/** If defined, output will be paginated with this many items per page. If undefined show all entries with scrollbar */
	entriesPerPage?: number;
	isExpanded: boolean;
}

/** Component for showing a table inside a dashboard widget */
export const WidgetTable: FC<IWidgetTableProps> = memo((props: IWidgetTableProps) => {
	const { noRecordsMessage, children, entriesPerPage, isExpanded } = props;
	const entriesToShowPerPage = entriesPerPage || 2;
	const expandedHeight = useBreakpointValue({ base: "50vh", xl: "60vh", "2xl": "65vh", "3xl": "70vh" });

	if (React.Children.count(children) === 0) {
		return (
			<Center
				fontStyle="italic"
				h={isExpanded ? "50vh" : "unset"}
				fontSize={isExpanded ? "1.75em" : "1.25em"}
			>
				{noRecordsMessage}
			</Center>
		);
	}

	return (
		<List>
			{entriesPerPage ? (
				<PaginatedList entriesPerPage={entriesToShowPerPage} h="2.25em">
					{children}
				</PaginatedList>
			) : (
				<Box h={isExpanded ? expandedHeight : "unset"} overflowX="hidden">
					{children}
				</Box>
			)}
		</List>
	);
});
